import { captureException } from '@sentry/nextjs';
import { AnalyticsDelegator } from 'lib/analytics/AnalyticsDelegator';
import { ConvergeAnalytics } from 'lib/analytics/converge';
import { GA4Analytics } from 'lib/analytics/ga4';
import { PolarAnalytics } from 'lib/analytics/polar';
import type { AnalyticsSink } from 'lib/analytics/types';
import { VercelAnalytics } from 'lib/analytics/vercel';
import { env } from 'lib/constants/env';
import type { ScriptManager } from 'lib/scriptManager';
import { create } from 'zustand';

interface AnalyticsDelegatorState {
  analyticsDelegator: AnalyticsDelegator;
  sinksRegistered: boolean;
  registerSinks: (scriptManager: ScriptManager) => void;
}

const createDelegator = () => {
  const delegator = new AnalyticsDelegator();
  try {
    /* Eager register Vercel */
    const vercel = new VercelAnalytics();
    vercel.setEnableCondition(() => true);
    delegator.registerSink(vercel);
  } catch (err) {
    captureException(err as Error);
  }
  return delegator;
};

export const useAnalyticsDelegatorStore = create<AnalyticsDelegatorState>()(
  (set, get) => ({
    analyticsDelegator: createDelegator(),
    sinksRegistered: false,
    registerSinks: (scriptManager) => {
      if (get().sinksRegistered) return;
      set({ sinksRegistered: true });

      const { analyticsDelegator } = get();
      analyticsDelegator.registerScriptManager(scriptManager);

      /* Defensively register each sink to prevent one failure
       * impacting the others. */
      const register = (build: () => AnalyticsSink) => {
        try {
          analyticsDelegator.registerSink(build());
        } catch (err) {
          captureException(err as Error);
        }
      };

      if (env.NEXT_PUBLIC_GA_ID) {
        register(() => {
          const ga4 = new GA4Analytics(scriptManager);
          ga4.setEnableCondition(() =>
            scriptManager.getCondition('cookiesAccepted'),
          );
          return ga4;
        });
      }

      if (env.NEXT_PUBLIC_CONVERGE_PIXEL_URL) {
        register(() => {
          const converge = new ConvergeAnalytics(scriptManager);
          converge.setEnableCondition(() =>
            scriptManager.getCondition('cookiesAccepted'),
          );
          return converge;
        });
      }

      if (env.NEXT_PUBLIC_POLAR_PIXEL_ENDPOINT) {
        register(() => {
          const polar = new PolarAnalytics();
          polar.setEnableCondition(() =>
            scriptManager.getCondition('cookiesAccepted'),
          );
          return polar;
        });
      }
    },
  }),
);
